// Messages — surfaces captured iMessage / SMS / WhatsApp conversations
// ingested by the MacBook bridge daemon, plus the Brain's daily message
// summary. Two-column layout (thread list left, chat detail right)
// mirrors Meetings.tsx / Inbox.tsx PR #24 polish.
//
// READ-ONLY surface (N1 — Phase 1 has zero outbound on these channels).
// There is no compose box, no send button, no reply affordance anywhere
// on this page by design. Joel keeps texting normally; we only display
// what the bridge captured. RLS (owner_id = auth.uid()) scopes every read.
//
// Data:
//   - public.messages — the unified iMessage/SMS/WhatsApp table. We group
//     rows by thread_id client-side into conversations and render each as
//     chat bubbles (outbound right / forest tint, inbound left / neutral).
//   - public.message_daily_summaries — the Brain's once-a-day digest. This
//     table is created concurrently; the query degrades to an empty state
//     when it returns nothing (or errors, e.g. table not yet migrated).

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import { MessageSquare, Smartphone, Sparkles } from 'lucide-react';
import GlassCard from '@/components/shared/GlassCard';
import { supabase } from '@/lib/supabase';
import { firstNonBlank } from '@/utils/firstNonBlank';

type SourceChannel = 'imessage' | 'sms' | 'whatsapp';

type MessageRow = {
  id: string;
  source_channel: SourceChannel;
  thread_id: string;
  thread_label: string | null;
  counterparty_handle: string | null;
  counterparty_name: string | null;
  direction: 'inbound' | 'outbound';
  body: string | null;
  occurred_at: string;
};

const MESSAGE_SELECT =
  'id, source_channel, thread_id, thread_label, counterparty_handle, counterparty_name, direction, body, occurred_at';

async function fetchMessages(): Promise<MessageRow[]> {
  const { data, error } = await supabase
    .from('messages')
    .select(MESSAGE_SELECT)
    // RLS scopes to owner_id = auth.uid(); this is a pure read surface.
    .order('occurred_at', { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data as unknown as MessageRow[]) ?? [];
}

type DailySummaryRow = {
  id: string;
  summary_date: string;
  message_count: number;
  channels: string[] | null;
  summary: string;
  learnings: string | null;
  created_at: string;
};

const DAILY_SUMMARY_SELECT =
  'id, summary_date, message_count, channels, summary, learnings, created_at';

// This table is being created concurrently. We tolerate it not existing
// yet: any error (including "relation does not exist") degrades to an
// empty list so the page renders its empty state instead of throwing.
async function fetchDailySummaries(): Promise<DailySummaryRow[]> {
  const { data, error } = await supabase
    .from('message_daily_summaries')
    .select(DAILY_SUMMARY_SELECT)
    .order('summary_date', { ascending: false })
    .limit(7);
  if (error) return [];
  return (data as unknown as DailySummaryRow[]) ?? [];
}

// Per-channel badge tone — a small color distinction so a thread's
// medium reads at a glance.
function channelTone(channel: SourceChannel): { bg: string; text: string; label: string } {
  switch (channel) {
    case 'imessage':
      return { bg: 'bg-forest-soft', text: 'text-forest', label: 'iMessage' };
    case 'whatsapp':
      return { bg: 'bg-sage/10', text: 'text-sage', label: 'WhatsApp' };
    case 'sms':
    default:
      return { bg: 'bg-learning/10', text: 'text-learning', label: 'SMS' };
  }
}

type Thread = {
  threadId: string;
  label: string;
  channel: SourceChannel;
  messages: MessageRow[];
  latest: MessageRow;
};

// Group flat message rows (newest-first) into per-thread conversations.
// Threads are ordered by their most-recent message; within a thread the
// messages are sorted oldest→newest so the chat reads top-to-bottom.
function groupThreads(rows: MessageRow[]): Thread[] {
  const byThread = new Map<string, MessageRow[]>();
  for (const row of rows) {
    const existing = byThread.get(row.thread_id);
    if (existing) existing.push(row);
    else byThread.set(row.thread_id, [row]);
  }
  const threads: Thread[] = [];
  for (const [threadId, msgs] of byThread) {
    // Incoming rows are newest-first (occurred_at desc); latest is [0].
    const latest = msgs[0];
    const ascending = [...msgs].sort(
      (a, b) => new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
    );
    threads.push({
      threadId,
      // firstNonBlank, not ?? — macOS writes '' (not null) as the 1:1 chat
      // display name, and '' would otherwise shadow the resolved contact name.
      label:
        firstNonBlank(latest.thread_label, latest.counterparty_name, latest.counterparty_handle) ??
        '(unknown)',
      channel: latest.source_channel,
      messages: ascending,
      latest,
    });
  }
  threads.sort(
    (a, b) => new Date(b.latest.occurred_at).getTime() - new Date(a.latest.occurred_at).getTime(),
  );
  return threads;
}

const Messages = () => {
  const messagesQ = useQuery({ queryKey: ['messages', 'all'], queryFn: fetchMessages });
  const summariesQ = useQuery({
    queryKey: ['message_daily_summaries'],
    queryFn: fetchDailySummaries,
  });

  const threads = useMemo(() => groupThreads(messagesQ.data ?? []), [messagesQ.data]);

  // Selected thread (left list → right detail). Default to the first
  // thread once data lands; fall back to null on an empty list.
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const selectedThread = useMemo(() => {
    if (threads.length === 0) return null;
    const match = threads.find((t) => t.threadId === selectedThreadId);
    return match ?? threads[0];
  }, [threads, selectedThreadId]);

  const summaries = summariesQ.data ?? [];
  const totalMessages = messagesQ.data?.length ?? 0;

  return (
    <div className="space-y-6">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex items-start justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-serif font-semibold tracking-tight text-foreground">
            Messages
          </h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Everything captured from iMessage, SMS and WhatsApp — read-only (N1).
          </p>
        </div>
      </motion.header>

      {/* Daily summary */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-forest" />
          <h2 className="text-lg font-medium tracking-tight">Daily summary</h2>
        </div>
        {summariesQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading summary…</p>
        ) : summaries.length === 0 ? (
          <GlassCard hover={false} variant="inset">
            <p className="text-sm text-muted-foreground">
              No daily summary yet — Reverra writes one each day from your messages.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {summaries.map((s) => (
              <DailySummaryCard key={s.id} summary={s} />
            ))}
          </div>
        )}
      </section>

      {/* Conversations */}
      <section>
        {messagesQ.isLoading && <p className="text-sm text-muted-foreground">Loading messages…</p>}
        {messagesQ.isError && (
          <GlassCard hover={false} className="neo-alert">
            <p className="text-sm text-alert">
              Failed to load messages: {(messagesQ.error as Error).message}
            </p>
          </GlassCard>
        )}

        {!messagesQ.isLoading && !messagesQ.isError && threads.length === 0 && (
          <GlassCard hover={false} variant="inset">
            <p className="text-sm text-muted-foreground">
              No messages captured yet. iMessage, SMS and WhatsApp flow in from the MacBook bridge
              while it's awake and plugged in.
            </p>
            <p className="mt-2 text-[11px] text-muted-foreground/80">
              Read-only — the agent never sends or replies on these channels (N1).
            </p>
          </GlassCard>
        )}

        {threads.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 lg:h-[calc(100vh-14rem)] lg:min-h-[560px]">
            {/* Thread list column */}
            <div className="lg:col-span-2 space-y-3 lg:overflow-y-auto lg:pr-2">
              <h2 className="text-sm font-semibold text-foreground mt-1">
                Conversations
                <span className="ml-2 text-[11px] font-mono text-muted-foreground font-normal">
                  {threads.length}
                </span>
              </h2>
              <div className="space-y-3">
                <AnimatePresence mode="popLayout" initial={false}>
                  {threads.map((t) => (
                    <motion.div
                      key={t.threadId}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <ThreadListItem
                        thread={t}
                        selected={t.threadId === selectedThread?.threadId}
                        onSelect={() => setSelectedThreadId(t.threadId)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>

            {/* Conversation detail column — `min-w-0` lets the column shrink
                below its intrinsic content width so long unbroken tokens in
                a bubble don't blow the layout out at narrow viewports. */}
            <div className="lg:col-span-3 lg:h-full lg:overflow-y-auto min-w-0">
              {selectedThread ? (
                <ThreadDetail thread={selectedThread} />
              ) : (
                <GlassCard hover={false} variant="inset" className="h-full min-h-[400px]">
                  <p className="text-sm text-muted-foreground">Select a conversation to view.</p>
                </GlassCard>
              )}
            </div>
          </div>
        )}
      </section>

      {totalMessages > 0 && (
        <p className="text-[11px] text-muted-foreground/80">
          Showing the {totalMessages} most recent captured messages — read-only, never sent or
          replied to (N1).
        </p>
      )}
    </div>
  );
};

function DailySummaryCard({ summary }: { summary: DailySummaryRow }) {
  const channels = summary.channels ?? [];
  const summaryDate = (() => {
    // summary_date is a DATE ("2026-06-24"); parse as local midnight so the
    // displayed day doesn't shift across the timezone boundary.
    const d = new Date(`${summary.summary_date}T00:00:00`);
    return Number.isNaN(d.getTime()) ? summary.summary_date : format(d, 'EEE, MMM d');
  })();
  return (
    <GlassCard hover={false}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-sm font-mono text-muted-foreground">{summaryDate}</span>
        <span className="text-[11px] font-mono text-muted-foreground">
          · {summary.message_count} message{summary.message_count === 1 ? '' : 's'}
        </span>
        <span className="ml-auto flex items-center gap-1.5 flex-wrap">
          {channels.map((c) => {
            const tone = channelTone(c as SourceChannel);
            return (
              <span
                key={c}
                className={`neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} shrink-0`}
              >
                {tone.label}
              </span>
            );
          })}
        </span>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm text-foreground leading-relaxed">
        {summary.summary}
      </p>
      {summary.learnings && (
        <div className="mt-3 neo-inset p-3">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
            Learnings
          </p>
          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground leading-relaxed">
            {summary.learnings}
          </p>
        </div>
      )}
    </GlassCard>
  );
}

type ThreadListItemProps = {
  thread: Thread;
  selected: boolean;
  onSelect: () => void;
};

function ThreadListItem({ thread, selected, onSelect }: ThreadListItemProps) {
  const tone = channelTone(thread.channel);
  const preview = thread.latest.body ?? '(no text)';
  return (
    <GlassCard
      hover={false}
      ariaPressed={selected}
      className={`cursor-pointer transition-all relative ${
        selected
          ? 'neo-selected before:absolute before:left-1 before:top-3 before:bottom-3 before:w-1 before:rounded-r before:bg-forest'
          : ''
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center gap-2 mb-1">
        <Smartphone className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground truncate">{thread.label}</span>
        <span
          className={`ml-auto neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} shrink-0`}
        >
          {tone.label}
        </span>
      </div>
      <p className="text-sm text-muted-foreground line-clamp-1">
        {thread.latest.direction === 'outbound' && <span className="text-forest/80">You: </span>}
        {preview}
      </p>
      <p className="text-[11px] font-mono text-muted-foreground mt-1">
        {formatDistanceToNow(new Date(thread.latest.occurred_at), { addSuffix: true })}
      </p>
    </GlassCard>
  );
}

function ThreadDetail({ thread }: { thread: Thread }) {
  const tone = channelTone(thread.channel);
  return (
    <GlassCard hover={false} className="flex flex-col min-w-0">
      {/* Header */}
      <div className="pb-4 border-b border-border">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <MessageSquare className="w-4 h-4 text-muted-foreground shrink-0" />
          <h3 className="text-base font-semibold text-foreground leading-snug break-words">
            {thread.label}
          </h3>
          <span
            className={`ml-auto neo-chip text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${tone.bg} ${tone.text} shrink-0`}
          >
            {tone.label}
          </span>
        </div>
        <p className="text-[11px] font-mono text-muted-foreground">
          {thread.messages.length} message{thread.messages.length === 1 ? '' : 's'}
        </p>
      </div>

      {/* Chat bubbles */}
      <div className="py-4 flex-1 space-y-3">
        {thread.messages.map((m) => (
          <ChatBubble key={m.id} message={m} />
        ))}
      </div>

      <p className="mt-auto pt-3 text-[11px] text-muted-foreground/80 self-start">
        Read-only — captured for context. The agent never sends or replies here (N1).
      </p>
    </GlassCard>
  );
}

function ChatBubble({ message }: { message: MessageRow }) {
  const outbound = message.direction === 'outbound';
  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`flex flex-col max-w-[78%] min-w-0 ${outbound ? 'items-end' : 'items-start'}`}
      >
        <div
          className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed break-words whitespace-pre-wrap ${
            outbound
              ? 'bg-forest-soft text-forest rounded-br-sm'
              : 'bg-secondary text-foreground rounded-bl-sm'
          }`}
        >
          {message.body ?? '(no text)'}
        </div>
        <span className="mt-0.5 px-1 text-[10px] font-mono text-muted-foreground">
          {format(new Date(message.occurred_at), 'MMM d, h:mm a')}
        </span>
      </div>
    </div>
  );
}

export default Messages;
